import { defineStore } from 'pinia'
import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
  inMemoryPersistence,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  updateProfile,
  updateEmail,
  type User as FirebaseUser,
} from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { auth, firestore, functions } from '../firebase'
import { doc, setDoc, getDoc, onSnapshot, Timestamp } from 'firebase/firestore'
import { useCommunityStore } from './Community'
import type { UserDataRead } from '../utils/types'

const initializeUserFunc = httpsCallable(functions, 'initializeUser')

const AUTH_PERSISTENCE: 'session' | 'local' = 'local'

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as UserDataRead | null,
    isAuthenticated: false,
    needsEmailVerification: false,
    isAdmin: null as boolean | null,
    isLoading: false,
    isInitialized: false,
  }),

  getters: {
    getDisplayName: (state) => state.user?.displayName || 'User',
  },

  actions: {
    async setUser(firebaseUser: FirebaseUser) {
      console.log('Setting user, UID:', firebaseUser.uid)
      try {
        const userDocRef = doc(firestore, 'users', firebaseUser.uid)
        const userDoc = await getDoc(userDocRef)
        let firestoreData: UserDataRead | undefined

        if (userDoc.exists()) {
          firestoreData = userDoc.data() as UserDataRead
        }

        this.user = {
          id: firebaseUser.uid,
          userId: firebaseUser.uid,
          created_at: firestoreData?.created_at || Timestamp.fromDate(new Date(0)),
          updated_at: null,
          displayName: firebaseUser.displayName || firestoreData?.displayName || 'User',
          likeCount: firestoreData?.likeCount || 0,
          reportsReceived: firestoreData?.reportsReceived || 0,
          appealStatus: firestoreData?.appealStatus || 'none',
          fcmToken: firestoreData?.fcmToken || null,
          notificationPreferences: firestoreData?.notificationPreferences || {
            postLikes: true,
            commentLikes: true,
            replyLikes: true,
            comments: true,
            replies: true,
            answers: true,
            reports: true,
          },
          accountStatus: firestoreData?.accountStatus || 'active',
          email: firebaseUser.email || '',
          emailVerified: firebaseUser.emailVerified,
        }
        this.isAuthenticated = true
        this.needsEmailVerification = !firebaseUser.emailVerified
        console.log('User set:', this.user)

        onSnapshot(userDocRef, (doc) => {
          if (doc.exists()) {
            const data = doc.data() as UserDataRead
            this.user = {
              ...this.user!,
              ...data,
              displayName: data.displayName || this.user!.displayName,
              email: this.user!.email,
              emailVerified: this.user!.emailVerified,
            }
            console.log('User data updated via snapshot:', this.user)
          }
        })
      } catch (error) {
        console.error('Firestore 사용자 데이터 동기화 실패:', error)
        this.user = {
          id: firebaseUser.uid,
          userId: firebaseUser.uid,
          created_at: Timestamp.fromDate(new Date(0)),
          updated_at: null,
          likeCount: 0,
          reportsReceived: 0,
          appealStatus: 'none',
          fcmToken: null,
          notificationPreferences: {
            postLikes: true,
            commentLikes: true,
            replyLikes: true,
            comments: true,
            replies: true,
            answers: true,
            reports: true,
          },
          accountStatus: 'active',
          displayName: firebaseUser.displayName || 'User',
          email: firebaseUser.email || '',
          emailVerified: firebaseUser.emailVerified,
        }
        this.isAuthenticated = true
        this.needsEmailVerification = !firebaseUser.emailVerified
        console.log('User set (fallback):', this.user)
      }
    },

    async initializeAuth() {
      if (this.isInitialized) return
      this.isLoading = true
      return new Promise<void>(async (resolve) => {
        try {
          // Try persistence with graceful fallbacks for restricted webviews
          const candidates = [
            AUTH_PERSISTENCE === 'local' ? browserLocalPersistence : browserSessionPersistence,
            AUTH_PERSISTENCE === 'local' ? browserSessionPersistence : browserLocalPersistence,
            inMemoryPersistence,
          ]

          let persistenceApplied = false
          for (const candidate of candidates) {
            try {
              await setPersistence(auth, candidate)
              persistenceApplied = true
              break
            } catch (err) {
              console.warn('setPersistence 실패, 다음 후보로 폴백 시도:', err)
            }
          }
          if (!persistenceApplied) {
            console.warn('어떠한 persistence도 적용하지 못했지만 계속 진행합니다.')
          }

          onAuthStateChanged(auth, async (firebaseUser) => {
            try {
              console.log('onAuthStateChanged 호출, 사용자:', firebaseUser)
              if (firebaseUser) {
                await this.setUser(firebaseUser)
                await this.checkAdminStatus()
              } else {
                console.log('No authenticated user, resetting state')
                this.resetState()
              }
            } catch (cbErr) {
              console.error('onAuthStateChanged 처리 중 오류:', cbErr)
              this.resetState()
            } finally {
              this.isInitialized = true
              this.isLoading = false
              resolve()
            }
          })
        } catch (error) {
          console.error('인증 초기화 루틴 전체 실패, 기본 상태로 진행:', error)
          this.resetState()
          this.isInitialized = true
          this.isLoading = false
          resolve()
        }
      })
    },

    resetState() {
      this.user = null
      this.isAuthenticated = false
      this.needsEmailVerification = false
      this.isAdmin = null
    },

    async checkAdminStatus() {
      if (!this.user || this.isAdmin !== null) return
      this.isLoading = true
      try {
        const idTokenResult = await auth.currentUser?.getIdTokenResult(true)
        console.log('ID Token Claims:', idTokenResult?.claims)
        this.isAdmin = idTokenResult?.claims.isAdmin === true
      } catch (error) {
        console.error('관리자 상태 확인 실패:', error)
        this.isAdmin = false
      } finally {
        this.isLoading = false
      }
    },

    getUserId() {
      if (!this.user) {
        throw new Error('User not authenticated')
      }
      return this.user.userId
    },

    handleError(error: unknown): string {
      if (error && typeof error === 'object' && 'code' in error) {
        const code = (error as { code: string }).code
        switch (code) {
          case 'auth/weak-password':
            return '비밀번호가 너무 약합니다. 8자 이상, 대문자, 소문자, 숫자, 특수문자를 포함해야 합니다.'
          case 'auth/invalid-email':
            return '잘못된 이메일 주소입니다.'
          case 'auth/email-already-in-use':
            return '이미 사용 중인 이메일입니다.'
          case 'auth/wrong-password':
            return '비밀번호가 틀렸습니다.'
          case 'auth/user-not-found':
            return '사용자를 찾을 수 없습니다.'
          case 'auth/too-many-requests':
            return '시도가 너무 많습니다. 나중에 다시 시도해주세요.'
          case 'auth/user-disabled':
            return '이 계정은 비활성화되었습니다. 지원팀에 문의하세요.'
          case 'auth/invalid-credential':
            return '잘못된 인증 정보입니다.'
          case 'auth/operation-not-allowed':
            return '이 작업은 허용되지 않습니다.'
          case 'auth/requires-recent-login':
            return '보안상의 이유로 다시 로그인해야 합니다.'
          default:
            console.error('Unhandled Firebase error:', error)
            return '알 수 없는 오류가 발생했습니다. 다시 시도해주세요.'
        }
      }
      console.error('Unknown error:', error)
      return '문제가 발생했습니다. 다시 시도해주세요.'
    },

    async register(email: string, password: string) {
      this.isLoading = true
      try {
        const passwordError = this.validatePasswordStrength(password)
        if (passwordError) throw new Error(passwordError)

        const userCredential = await createUserWithEmailAndPassword(auth, email, password)
        const firebaseUser = userCredential.user

        const result = await initializeUserFunc({} as Record<string, never>)
        console.log('Initialize user result:', result.data)

        await sendEmailVerification(firebaseUser)
        await this.setUser(firebaseUser)
      } catch (error: unknown) {
        console.error('Registration error:', error)
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    validatePasswordStrength(password: string): string | null {
      const minLength = 8
      const hasUpperCase = /[A-Z]/.test(password)
      const hasLowerCase = /[a-z]/.test(password)
      const hasNumber = /\d/.test(password)
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password)

      if (password.length < minLength) {
        return '비밀번호는 최소 8자 이상이어야 합니다.'
      }
      if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecialChar) {
        return '비밀번호는 대문자, 소문자, 숫자, 특수문자를 포함해야 합니다.'
      }
      return null
    },

    async login(email: string, password: string) {
      this.isLoading = true
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        await this.setUser(userCredential.user)
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async logout() {
      this.isLoading = true
      try {
        await signOut(auth)
        this.resetState()
        const communityStore = useCommunityStore()
        communityStore.cleanupAllListeners()
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async resetPassword(email: string) {
      this.isLoading = true
      try {
        await sendPasswordResetEmail(auth, email)
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async changePassword(currentPassword: string, newPassword: string) {
      this.isLoading = true
      try {
        const passwordError = this.validatePasswordStrength(newPassword)
        if (passwordError) throw new Error(passwordError)

        const user = auth.currentUser
        if (!user || !user.email) throw new Error('User not found.')
        await this.reauthenticateUser(user, currentPassword)
        await updatePassword(user, newPassword)
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async deleteAccount(currentPassword: string) {
      this.isLoading = true
      try {
        const user = auth.currentUser
        if (!user || !user.email) throw new Error('User not found.')
        await this.reauthenticateUser(user, currentPassword)
        await deleteUser(user)
        this.resetState()
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async reauthenticateUser(user: FirebaseUser, currentPassword: string) {
      const credential = EmailAuthProvider.credential(user.email!, currentPassword)
      await reauthenticateWithCredential(user, credential)
    },

    async updateDisplayName(newDisplayName: string) {
      this.isLoading = true
      try {
        const user = auth.currentUser
        if (!user) throw new Error('No authenticated user found.')
        await updateProfile(user, { displayName: newDisplayName })
        const userDocRef = doc(firestore, 'users', user.uid)
        await setDoc(userDocRef, { displayName: newDisplayName }, { merge: true })
        await this.setUser(user)
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async updateEmail(currentPassword: string, newEmail: string) {
      this.isLoading = true
      try {
        const user = auth.currentUser
        if (!user || !user.email) throw new Error('No authenticated user found.')
        if (!newEmail.includes('@')) throw new Error('Invalid email address.')
        await this.reauthenticateUser(user, currentPassword)
        await updateEmail(user, newEmail.trim())
        await sendEmailVerification(user)
        await this.setUser(user)
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },

    async resendVerificationEmail() {
      this.isLoading = true
      try {
        const user = auth.currentUser
        if (user && !user.emailVerified) {
          await sendEmailVerification(user)
          return 'Verification email sent successfully.'
        } else if (user && user.emailVerified) {
          return 'Email is already verified.'
        } else {
          throw new Error('No authenticated user found.')
        }
      } catch (error) {
        throw new Error(this.handleError(error))
      } finally {
        this.isLoading = false
      }
    },
  },
})
